/**
 * Brown Butters — curved marquee strip.
 *
 * The band is an SVG path lifted straight out of Figma ("Vector 4" / "Vector 8"), and the
 * marquee text rides that same path via <textPath>. `startOffset` is the only thing that can
 * slide text along a path, and it is not a CSS property, so the scroll has to be driven here.
 *
 * The loop is seamless because every repetition of the caption is an identical <tspan>:
 * once the offset has travelled exactly one repetition it is reset, which is visually
 * indistinguishable from where it started. Copies are cloned until the run of text is long
 * enough to cover the whole path plus that one spare repetition.
 *
 * Dependency-free, pauses off-screen, and stands still for prefers-reduced-motion.
 */

const ROOT_SELECTOR = '[data-mbb-wave]';
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {Set<() => void>} */
const teardowns = new Set();

/**
 * @param {SVGTextPathElement} textPath
 * @returns {(() => void) | null}
 */
function animate(textPath) {
  const root = textPath.closest(ROOT_SELECTOR);
  const href = textPath.getAttribute('href') || '';
  const path = href.startsWith('#') ? document.getElementById(href.slice(1)) : null;
  if (!(root instanceof HTMLElement) || !(path instanceof SVGPathElement)) return null;

  const first = textPath.querySelector('tspan');
  if (!(first instanceof SVGTSpanElement)) return null;

  // A hidden breakpoint variant measures as zero — leave it alone until it is shown.
  const unit = first.getComputedTextLength();
  if (!unit || unit <= 0) return null;

  const total = path.getTotalLength();
  let run = textPath.querySelectorAll('tspan').length * unit;
  while (run < total + unit && textPath.querySelectorAll('tspan').length < 40) {
    textPath.appendChild(first.cloneNode(true));
    run += unit;
  }

  const speed = Math.abs(parseFloat(root.dataset.speed || '40')) || 40;
  const forwards = root.dataset.direction === 'right';
  let offset = forwards ? -unit : 0;
  textPath.setAttribute('startOffset', String(offset));

  if (reduceMotion.matches) return null;

  let frame = 0;
  let last = 0;
  let running = false;

  const tick = (now) => {
    if (!running) return;
    const delta = last ? Math.min((now - last) / 1000, 0.1) : 0;
    last = now;
    offset += (forwards ? speed : -speed) * delta;
    if (offset <= -unit) offset += unit;
    if (offset >= 0) offset -= unit;
    textPath.setAttribute('startOffset', String(offset));
    frame = requestAnimationFrame(tick);
  };

  const start = () => {
    if (running) return;
    running = true;
    last = 0;
    frame = requestAnimationFrame(tick);
  };

  const stop = () => {
    running = false;
    cancelAnimationFrame(frame);
  };

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) start();
      else stop();
    }
  });
  observer.observe(root);

  return () => {
    stop();
    observer.disconnect();
  };
}

function init() {
  for (const teardown of teardowns) teardown();
  teardowns.clear();

  for (const textPath of document.querySelectorAll(`${ROOT_SELECTOR} textPath`)) {
    const teardown = animate(/** @type {SVGTextPathElement} */ (textPath));
    if (teardown) teardowns.add(teardown);
  }
}

let resizeTimer = 0;
const scheduleInit = () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(init, 150);
};

if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(init);
} else {
  init();
}

window.addEventListener('resize', scheduleInit);
document.addEventListener('shopify:section:load', scheduleInit);
document.addEventListener('shopify:section:unload', scheduleInit);
reduceMotion.addEventListener('change', scheduleInit);
